<template>
  <Modal
    :show="true"
    title="Edit Contact"
    headerVariant="blue"
    size="md"
    dialogMaxWidth="640px"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :zIndex="10060"
    :show-close-button="false"
    @close="closeModal"
  >
    <template #header>
      <div class="edit-header__text">
        <h2 class="edit-header__title">Edit Contact</h2>
        <p class="edit-header__subtitle">Update contact information</p>
      </div>
      <button type="button" class="edit-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #body>
      <Form id="edit-contact-form" class="form-body" @submit="editContact">
        <!-- First Name + Last Name -->
        <div class="field-grid-2">
          <Field
            id="fname"
            v-model="editContactObj.fname"
            :rules="{ required: ['First Name'] }"
            name="fname"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="First Name"
              placeholder="Enter first name"
              required
              :error="errorMessage"
              size="md"
            />
          </Field>
          <Field
            id="lname"
            v-model="editContactObj.lname"
            :rules="{ required: ['Last Name'] }"
            name="lname"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Last Name"
              placeholder="Enter last name"
              required
              :error="errorMessage"
              size="md"
            />
          </Field>
        </div>

        <!-- Job Title + Email -->
        <div class="field-grid-2">
          <Field
            id="jobTitle"
            v-model="editContactObj.jobTitle"
            :rules="{}"
            name="jobTitle"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Job Title"
              placeholder="Enter job title"
              :error="errorMessage"
              size="md"
            />
          </Field>
          <Field
            id="email"
            v-model="editContactObj.email"
            :rules="{ required: ['Email'], email: ['Email'] }"
            name="email"
            type="email"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Email"
              type="email"
              placeholder="Enter email address"
              required
              :error="errorMessage"
              size="md"
            />
          </Field>
        </div>

        <!-- Company -->
        <Field
          id="company"
          v-model="editContactObj.company"
          type="text"
          name="company"
          v-slot="{ field, errorMessage }"
        >
          <Input
            :model-value="field.value"
            @update:model-value="field.onChange($event)"
            @blur="field.onBlur"
            label="Company"
            placeholder="Enter company name"
            :error="errorMessage"
            size="md"
          />
        </Field>

        <!-- Website + Industry -->
        <div class="field-grid-2">
          <Field
            id="website"
            v-model="editContactObj.website"
            type="text"
            name="website"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Website"
              placeholder="Enter website URL"
              :error="errorMessage"
              size="md"
            />
          </Field>
          <Field
            id="industry"
            v-model="editContactObj.industry"
            type="text"
            name="industry"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Industry"
              placeholder="Enter industry"
              :error="errorMessage"
              size="md"
            />
          </Field>
        </div>

        <div class="phone-entry">
          <div class="phone-entry__number">
            <PhoneInput
              v-model="editContactObj.primaryPhone.number"
              label="Phone"
              defaultCountry="US"
              size="md"
            />
          </div>
          <div class="phone-entry__type">
            <Select
              v-model="editContactObj.primaryPhone.type"
              :options="phoneTypeOptions"
              size="md"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="additional-section-header">
            <label class="additional-section-label">Additional Emails</label>
            <button type="button" class="btn-new-field" @click="addAdditionalEmail">
              <Icon icon="tabler:plus" class="add-field-icon" />
              Add new email
            </button>
          </div>
          <div
            v-for="(_, index) in editContactObj.additionalEmails"
            :key="index"
            class="phone-entry phone-entry--with-delete"
          >
            <div class="phone-entry__number">
              <Input
                v-model="editContactObj.additionalEmails[index].email"
                type="email"
                placeholder="Enter email address"
                size="md"
              />
            </div>
            <div class="phone-entry__type">
              <Select
                v-model="editContactObj.additionalEmails[index].type"
                :options="emailTypeOptions"
                size="md"
              />
            </div>
            <button
              type="button"
              class="delete-field-btn phone-entry__delete"
              @click="removeAdditionalEmail(index)"
            >
              <Icon icon="mdi:delete-outline" />
            </button>
          </div>
        </div>

        <div class="form-group">
          <div class="additional-section-header">
            <label class="additional-section-label">Additional Phones</label>
            <button type="button" class="btn-new-field" @click="addPhone">
              <Icon icon="tabler:plus" class="add-field-icon" />
              Add new phone
            </button>
          </div>
          <div
            v-for="(_, index) in editContactObj.phones"
            :key="index"
            class="phone-entry phone-entry--with-delete"
          >
            <div class="phone-entry__number">
              <PhoneInput
                v-model="editContactObj.phones[index].number"
                defaultCountry="US"
                size="md"
              />
            </div>
            <div class="phone-entry__type">
              <Select
                v-model="editContactObj.phones[index].type"
                :options="phoneTypeOptions"
                size="md"
              />
            </div>
            <button
              type="button"
              class="delete-field-btn phone-entry__delete"
              @click="removePhone(index)"
            >
              <Icon icon="mdi:delete-outline" />
            </button>
          </div>
        </div>

        <div class="form-group">
          <Field
            id="linkedInUrl"
            v-model="editContactObj.linkedInUrl"
            :rules="{}"
            type="text"
            name="linkedInUrl"
            v-slot="{ field, errorMessage }"
          >
            <Input
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="LinkedIn Url"
              placeholder="Enter LinkedIn profile URL"
              :error="errorMessage"
              size="md"
            />
          </Field>
        </div>

        <div class="form-group">
          <TimezoneSelector v-model="editContactObj.timezone" label="Timezone" />
        </div>

        <!-- Custom Fields (schema-driven) -->
        <div v-if="customFieldDefs?.length" class="form-group">
          <label class="additional-section-label">Custom Fields</label>
          <div class="custom-fields-list">
            <div v-for="def in customFieldDefs" :key="def.id" class="custom-field-entry">
              <!-- Text / URL / Number -->
              <Input
                v-if="['text', 'url', 'number'].includes(def.fieldType)"
                :model-value="getCustomFieldValue(def.id)"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :placeholder="`Enter ${def.label.toLowerCase()}`"
                :type="def.fieldType === 'number' ? 'number' : 'text'"
                size="md"
              />
              <!-- Date -->
              <div v-else-if="def.fieldType === 'date'" class="custom-field-date">
                <label class="additional-section-label"
                  >{{ def.label }}{{ def.isRequired ? " *" : "" }}</label
                >
                <Input
                  :model-value="getCustomFieldValue(def.id)"
                  @update:model-value="setCustomFieldValue(def.id, $event)"
                  type="date"
                  size="md"
                />
              </div>
              <!-- Select -->
              <Select
                v-else-if="def.fieldType === 'select'"
                :model-value="getCustomFieldValue(def.id)"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :options="def.options.map((o) => ({ label: o, value: o }))"
                size="md"
              />
              <!-- Multi-select -->
              <MultiSelect
                v-else-if="def.fieldType === 'multi_select'"
                :model-value="getCustomFieldValue(def.id) || []"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :options="def.options.map((o) => ({ label: o, value: o }))"
                size="md"
              />
              <!-- Checkbox -->
              <div v-else-if="def.fieldType === 'checkbox'" class="custom-field-checkbox">
                <Checkbox
                  :model-value="!!getCustomFieldValue(def.id)"
                  @update:model-value="setCustomFieldValue(def.id, $event)"
                  :label="def.label"
                />
              </div>
            </div>
          </div>
        </div>
      </Form>
    </template>

    <template #footer>
      <Button
        type="button"
        variant="secondary"
        size="md"
        @click="closeModal"
        :disabled="contactIsLoading"
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="edit-contact-form"
        variant="primary"
        size="md"
        :loading="contactIsLoading"
        :disabled="contactIsLoading"
      >
        Save Changes
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Modal,
  MultiSelect,
  PhoneInput,
  Select,
  TimezoneSelector,
} from "@/components/ui";
import { useUpdateContact } from "@/composables/contacts";
import { useToast } from "@/composables/ui";
import { useActiveCustomFields } from "@/composables/settings/useCustomFields";
import { Field, Form, defineRule } from "vee-validate";

defineRule("email", (value) => {
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegExp.test(value) || "Email is not valid";
});

defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) return `${fieldName} is required`;
  return true;
});

const props = defineProps({
  selectedContact: { default: null },
});

const emit = defineEmits(["closeEditContactModal", "refreshContacts"]);

const store = useStore();
const account = computed(() => store.getters.account.data);
const toast = useToast();

const updateContactMutation = useUpdateContact();
const contactIsLoading = computed(() => updateContactMutation.isPending.value);

const { data: customFieldDefs } = useActiveCustomFields("contact");

const getCustomFieldValue = (fieldId) => {
  const entry = editContactObj.value.customFields?.find((f) => f.fieldId === fieldId);
  return entry?.value ?? null;
};

const setCustomFieldValue = (fieldId, value) => {
  const arr = editContactObj.value.customFields || [];
  const idx = arr.findIndex((f) => f.fieldId === fieldId);
  if (idx >= 0) {
    arr[idx] = { fieldId, value };
  } else {
    arr.push({ fieldId, value });
  }
  editContactObj.value.customFields = [...arr];
};

const editContactObj = ref({
  id: null,
  fname: null,
  lname: null,
  email: null,
  jobTitle: null,
  website: null,
  industry: null,
  linkedInUrl: null,
  additionalEmails: [{ email: null, type: "WORK" }],
  primaryPhone: { number: null, type: "MOBILE", routeType: "MOBILE" },
  phones: [{ number: null, type: "MOBILE", routeType: "MOBILE" }],
  customFields: [],
  company: null,
  organizationId: null,
  timezone: null,
});

const phoneTypeOptions = [
  { label: "Mobile", value: "MOBILE" },
  { label: "Direct", value: "DIRECT" },
  { label: "Work", value: "WORK" },
  { label: "Home", value: "HOME" },
  { label: "Other", value: "OTHER" },
];

const emailTypeOptions = [
  { label: "Work", value: "WORK" },
  { label: "Personal", value: "PERSONAL" },
  { label: "Other", value: "OTHER" },
];

const setValues = () => {
  if (!props.selectedContact) return;
  const contact = props.selectedContact;

  editContactObj.value.id = contact.id || null;
  editContactObj.value.fname = contact.fname || "";
  editContactObj.value.lname = contact.lname || "";
  editContactObj.value.jobTitle = contact.jobTitle || "";
  editContactObj.value.linkedInUrl = contact.linkedInUrl || "";
  editContactObj.value.timezone = contact.timezone || null;

  if (Array.isArray(contact.emails) && contact.emails.length > 0) {
    const primary = contact.emails.find((e) => e.isPrimary) || contact.emails[0];
    editContactObj.value.email = primary.email || "";
    const rest = contact.emails.filter((e) => e.email !== primary.email);
    editContactObj.value.additionalEmails =
      rest.length > 0
        ? rest.map((e) => ({ email: e.email || "", type: e.type || "WORK" }))
        : [{ email: null, type: "WORK" }];
  } else {
    editContactObj.value.email = contact.email || "";
    const additional = [];
    if (Array.isArray(contact.additionalEmails)) {
      contact.additionalEmails.forEach((addr) => {
        if (addr?.trim()) additional.push({ email: addr, type: "WORK" });
      });
    }
    editContactObj.value.additionalEmails =
      additional.length > 0 ? additional : [{ email: null, type: "WORK" }];
  }

  if (Array.isArray(contact.phones) && contact.phones.length > 0) {
    const [first, ...rest] = contact.phones;
    editContactObj.value.primaryPhone = {
      number: first.number || null,
      type: first.type || "MOBILE",
      routeType: first.routeType || (first.type === "MOBILE" ? "MOBILE" : "DIRECT"),
    };
    const mappedPhones = rest.map((p) => ({
      number: p.number || null,
      type: p.type || "MOBILE",
      routeType: p.routeType || (p.type === "MOBILE" ? "MOBILE" : "DIRECT"),
    }));
    editContactObj.value.phones =
      mappedPhones.length > 0
        ? mappedPhones
        : [{ number: null, type: "MOBILE", routeType: "MOBILE" }];
  } else {
    const allPhones = [];
    if (contact.mobileNumber) {
      allPhones.push({ number: contact.mobileNumber, type: "MOBILE", routeType: "MOBILE" });
    }
    if (contact.directNumber) {
      allPhones.push({ number: contact.directNumber, type: "DIRECT", routeType: "DIRECT" });
    }
    if (Array.isArray(contact.additionalPhoneNumbers)) {
      contact.additionalPhoneNumbers.forEach((num) => {
        if (num?.trim()) allPhones.push({ number: num, type: "OTHER", routeType: "DIRECT" });
      });
    }
    const [first, ...rest] = allPhones;
    editContactObj.value.primaryPhone = first || {
      number: null,
      type: "MOBILE",
      routeType: "MOBILE",
    };
    editContactObj.value.phones =
      rest.length > 0 ? rest : [{ number: null, type: "MOBILE", routeType: "MOBILE" }];
  }

  editContactObj.value.customFields = Array.isArray(contact.customFields)
    ? contact.customFields
    : [];
  editContactObj.value.company = contact.company?.name || "";
  editContactObj.value.website = contact.company?.website || "";
  editContactObj.value.industry = contact.company?.industry || "";
  editContactObj.value.organizationId =
    contact.organizationId || account.value?.organizationId || null;
};

const closeModal = () => emit("closeEditContactModal");
const addAdditionalEmail = () =>
  editContactObj.value.additionalEmails.push({ email: null, type: "WORK" });
const removeAdditionalEmail = (index) => editContactObj.value.additionalEmails.splice(index, 1);
const addPhone = () =>
  editContactObj.value.phones.push({ number: null, type: "MOBILE", routeType: "MOBILE" });
const removePhone = (index) => editContactObj.value.phones.splice(index, 1);

const editContact = async () => {
  // Drop blank schema-driven entries; pass legacy (no fieldId) entries through
  // untouched so older free-text custom fields aren't silently deleted.
  editContactObj.value.customFields = (editContactObj.value.customFields || []).filter((f) =>
    f.fieldId ? f.value !== null && f.value !== undefined && f.value !== "" : true
  );

  const emails = [];
  const primaryEmail = editContactObj.value.email?.trim();
  if (primaryEmail) {
    emails.push({ email: primaryEmail, type: "WORK", isPrimary: true });
  }
  editContactObj.value.additionalEmails
    .filter((e) => e.email?.trim())
    .forEach((e) => {
      emails.push({ email: e.email.trim(), type: e.type || "WORK", isPrimary: false });
    });

  const phones = [];
  const pp = editContactObj.value.primaryPhone;
  if (pp?.number?.trim()) {
    const type = (pp.type || "MOBILE").toUpperCase();
    phones.push({
      type,
      number: pp.number,
      routeType: pp.routeType || (type === "MOBILE" ? "MOBILE" : "DIRECT"),
    });
  }
  editContactObj.value.phones
    .filter((p) => p.number?.trim())
    .forEach((p) => {
      const type = (p.type || "MOBILE").toUpperCase();
      phones.push({
        type,
        number: p.number,
        routeType: p.routeType || (type === "MOBILE" ? "MOBILE" : "DIRECT"),
      });
    });

  const organizationId = account.value?.organizationId || editContactObj.value.organizationId;
  if (!organizationId) {
    toast.error("Organization ID is required");
    return;
  }

  try {
    const { additionalEmails: _ae, primaryPhone: _pp, ...contactData } = editContactObj.value;
    const result = await updateContactMutation.mutateAsync({
      ...contactData,
      email: primaryEmail || "",
      emails,
      phones: phones.length > 0 ? phones : [],
      organizationId,
    });
    if (result) {
      emit("refreshContacts");
      closeModal();
    }
  } catch {
    toast.error("Failed to update contact");
  }
};

onMounted(() => {
  if (props.selectedContact) setValues();
});
watch(
  () => props.selectedContact,
  (val) => {
    if (val) setValues();
  },
  { immediate: false }
);
</script>

<style scoped>
.edit-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.edit-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.edit-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.edit-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.edit-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.form-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: calc(75vh - 130px);
}

.form-body :deep(.ui-input__required) {
  font-size: 12px;
  line-height: 1;
  vertical-align: middle;
}

.field-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
}

.custom-fields-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.custom-field-entry {
  width: 100%;
}

.custom-field-date label,
.custom-field-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
}

.additional-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.additional-section-label {
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin: 0;
}

.btn-new-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: #3e3efe;
  cursor: pointer;
}

.btn-new-field:hover {
  color: #2d2dff;
}

.add-field-icon {
  font-size: 14px;
  color: #3e3efe;
}

.delete-field-btn {
  background: transparent;
  border: none;
  padding: 0;
  color: #dc2626;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  align-self: center;
}

.delete-field-btn:hover {
  color: #b91c1c;
}

.delete-field-btn .iconify {
  font-size: 15px;
  width: 15px;
  height: 15px;
}

.phone-entry {
  display: grid;
  grid-template-columns: 1fr 148px;
  gap: 10px;
  margin-bottom: 8px;
  align-items: center;
}

.phone-entry--with-delete {
  grid-template-columns: 1fr 148px auto;
}

.phone-entry__number {
  min-width: 0;
}

.phone-entry__type :deep(.ui-select__field) {
  height: 43px;
}

.phone-entry__delete {
  display: flex;
  align-items: center;
  height: 43px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
</style>
